import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, MapPin, Zap, Fuel, Upload, CheckCircle, AlertCircle,
  FileText, Send, Loader2
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExternalIntakePage() {
  const { token } = useParams();
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0); // 0=site, 1=requirement, 2=dg, 3=files, 4=done

  // Forms
  const [siteForm, setSiteForm] = useState({});
  const [reqForm, setReqForm] = useState({ use_case: 'backup', backup_hours_required: '', critical_load_kw: '', has_solar: false, constraints: '' });
  const [reqErrors, setReqErrors] = useState({});
  const [dgForm, setDGForm] = useState({ dg_capacity_kva: '', dg_make: '', dg_model: '', dg_age_years: '', dg_fuel_type: 'diesel', dg_notes: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/intake/${token}`);
      setSiteData(res.data.site);
      setSubmitted(res.data.submitted);
      setSiteForm({
        address: res.data.site.address || '',
        city: res.data.site.city || '',
        state: res.data.site.state || '',
        pincode: res.data.site.pincode || '',
        has_dg: res.data.site.has_dg || false,
        notes: res.data.site.notes || '',
      });
      setUploadedFiles(res.data.site.files || []);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Unable to load intake form';
      setError(msg);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSite = async () => {
    try {
      await axios.put(`${API}/api/intake/${token}/site`, siteForm);
      toast.success('Site details saved');
      setStep(1);
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleSaveReq = async () => {
    // Validate backup hours
    const newErrors = {};
    if (reqForm.backup_hours_required !== '') {
      const bh = Number(reqForm.backup_hours_required);
      if (isNaN(bh) || bh < 0.5 || bh > 6.0) newErrors.backup_hours = 'Must be between 0.5 and 6.0';
    }
    setReqErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Compute derived energy
    const derivedEnergy = (reqForm.critical_load_kw && reqForm.backup_hours_required)
      ? Number(reqForm.critical_load_kw) * Number(reqForm.backup_hours_required) : null;

    try {
      await axios.post(`${API}/api/intake/${token}/requirement`, {
        ...reqForm,
        backup_hours_required: reqForm.backup_hours_required ? Number(reqForm.backup_hours_required) : null,
        critical_load_kw: reqForm.critical_load_kw ? Number(reqForm.critical_load_kw) : null,
        expected_energy_kwh_per_day: derivedEnergy,
      });
      toast.success('Requirement saved');
      setStep(siteForm.has_dg ? 2 : 3);
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleSaveDG = async () => {
    try {
      await axios.post(`${API}/api/intake/${token}/dg-asset`, {
        ...dgForm,
        dg_capacity_kva: dgForm.dg_capacity_kva ? Number(dgForm.dg_capacity_kva) : null,
        dg_age_years: dgForm.dg_age_years ? Number(dgForm.dg_age_years) : null,
      });
      toast.success('DG details saved');
      setStep(3);
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('file_type', 'electricity_bill');
      const res = await axios.post(`${API}/api/intake/${token}/file`, form);
      setUploadedFiles(prev => [...prev, { ...res.data, original_filename: file.name }]);
      toast.success('File uploaded');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/intake/${token}/submit`);
      setSubmitted(true);
      setStep(4);
    } catch (e) { toast.error('Submission failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Page><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div></Page>;

  if (error) return (
    <Page>
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Link Unavailable</h2>
        <p className="text-slate-500">{error}</p>
      </div>
    </Page>
  );

  if (submitted || step === 4) return (
    <Page>
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
        <p className="text-slate-500 mb-4 max-w-md mx-auto">
          Your site data has been received successfully. Our team will review it and get back to you shortly.
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 max-w-sm mx-auto mb-6" data-testid="submission-ref">
          <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Submission Reference</p>
          <p className="text-sm font-mono text-slate-700">{siteData?.site_name || 'N/A'}</p>
          <p className="text-xs text-slate-400 mt-1">Site: {siteData?.company_name}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => { setSubmitted(false); setStep(3); }}
          data-testid="upload-more-btn"
        >
          <Upload className="w-3 h-3 mr-1" /> Upload More Files
        </Button>
      </div>
    </Page>
  );

  const steps = ['Site Details', 'Requirement', ...(siteForm.has_dg ? ['DG Details'] : []), 'Documents'];

  return (
    <Page>
      <Toaster position="top-right" />
      {/* Header */}
      <div className="mb-8">
        <p className="text-sm text-indigo-600 font-medium mb-1">{siteData.company_name}</p>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Site Data Collection</h1>
        <p className="text-sm text-slate-500">{siteData.site_name}</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center space-x-2 mb-8" data-testid="intake-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <div className={`flex-1 h-0.5 ${i <= step ? 'bg-indigo-500' : 'bg-slate-200'}`} />}
            <button
              onClick={() => i <= step && setStep(i)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                i === step ? 'bg-indigo-600 text-white' : i < step ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-400'
              }`}
            >
              <span>{i + 1}. {s}</span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Step 0: Site Details */}
      {step === 0 && (
        <Card title="Confirm Site Details" icon={Building2}>
          <div className="space-y-4">
            <div><Label>Address</Label><Input value={siteForm.address} onChange={e => setSiteForm({...siteForm, address: e.target.value})} className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>City</Label><Input value={siteForm.city} onChange={e => setSiteForm({...siteForm, city: e.target.value})} className="mt-1" /></div>
              <div><Label>State</Label><Input value={siteForm.state} onChange={e => setSiteForm({...siteForm, state: e.target.value})} className="mt-1" /></div>
              <div><Label>Pincode</Label><Input value={siteForm.pincode} onChange={e => setSiteForm({...siteForm, pincode: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" checked={siteForm.has_dg} onChange={e => setSiteForm({...siteForm, has_dg: e.target.checked})} id="has_dg_ext" className="rounded" />
              <Label htmlFor="has_dg_ext">This site has a Diesel Generator (DG set)</Label>
            </div>
            <div><Label>Additional Notes</Label><Input value={siteForm.notes} onChange={e => setSiteForm({...siteForm, notes: e.target.value})} className="mt-1" /></div>
            <div className="flex justify-end pt-4 border-t"><Button onClick={handleSaveSite} className="bg-indigo-600 hover:bg-indigo-700">Save & Continue</Button></div>
          </div>
        </Card>
      )}

      {/* Step 1: Requirement */}
      {step === 1 && (
        <Card title="Backup / Energy Requirement" icon={Zap}>
          <div className="space-y-4">
            <div>
              <Label>Primary Use Case</Label>
              <Select value={reqForm.use_case} onValueChange={v => setReqForm({...reqForm, use_case: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="backup">Backup Power</SelectItem>
                  <SelectItem value="dg_displacement">DG Displacement</SelectItem>
                  <SelectItem value="peak_shaving">Peak Shaving</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Backup Hours Needed</Label>
                <Input type="number" step="0.5" min="0.5" max="6.0" value={reqForm.backup_hours_required}
                  onChange={e => { setReqForm({...reqForm, backup_hours_required: e.target.value}); setReqErrors({}); }}
                  placeholder="0.5 - 6.0"
                  className={`mt-1 ${reqErrors.backup_hours ? 'border-red-400' : ''}`} data-testid="ext-backup-hours" />
                {reqErrors.backup_hours && <p className="text-xs text-red-500 mt-1">{reqErrors.backup_hours}</p>}
              </div>
              <div><Label>Critical Load (kW)</Label><Input type="number" value={reqForm.critical_load_kw} onChange={e => setReqForm({...reqForm, critical_load_kw: e.target.value})} placeholder="e.g., 200" className="mt-1" /></div>
            </div>
            {/* Derived energy (read-only) */}
            {reqForm.critical_load_kw && reqForm.backup_hours_required && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <p className="text-xs text-slate-400">Estimated Daily Energy</p>
                <p className="text-sm font-semibold text-slate-700">
                  {(Number(reqForm.critical_load_kw) * Number(reqForm.backup_hours_required)).toFixed(1)} kWh/day
                </p>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <input type="checkbox" checked={reqForm.has_solar} onChange={e => setReqForm({...reqForm, has_solar: e.target.checked})} id="solar_ext" className="rounded" />
              <Label htmlFor="solar_ext">Site has solar panels</Label>
            </div>
            <div><Label>Constraints</Label><Input value={reqForm.constraints} onChange={e => setReqForm({...reqForm, constraints: e.target.value})} placeholder="Space limitations, noise, budget..." className="mt-1" /></div>
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button onClick={handleSaveReq} className="bg-indigo-600 hover:bg-indigo-700">Save & Continue</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: DG Details */}
      {step === 2 && (
        <Card title="DG Set Details" icon={Fuel}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>DG Capacity (kVA)</Label><Input type="number" value={dgForm.dg_capacity_kva} onChange={e => setDGForm({...dgForm, dg_capacity_kva: e.target.value})} className="mt-1" /></div>
              <div><Label>Make</Label><Input value={dgForm.dg_make} onChange={e => setDGForm({...dgForm, dg_make: e.target.value})} placeholder="e.g., Cummins" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Model</Label><Input value={dgForm.dg_model} onChange={e => setDGForm({...dgForm, dg_model: e.target.value})} className="mt-1" /></div>
              <div><Label>Age (years)</Label><Input type="number" value={dgForm.dg_age_years} onChange={e => setDGForm({...dgForm, dg_age_years: e.target.value})} className="mt-1" /></div>
            </div>
            <div>
              <Label>Fuel Type</Label>
              <Select value={dgForm.dg_fuel_type} onValueChange={v => setDGForm({...dgForm, dg_fuel_type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="diesel">Diesel</SelectItem><SelectItem value="png">PNG</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={dgForm.dg_notes} onChange={e => setDGForm({...dgForm, dg_notes: e.target.value})} className="mt-1" /></div>
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleSaveDG} className="bg-indigo-600 hover:bg-indigo-700">Save & Continue</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 3: Files */}
      {step === 3 && (
        <Card title="Upload Documents" icon={Upload}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload electricity bills, DG logs, or any relevant documents.</p>
            <div className="border-2 border-dashed border-slate-200 rounded-sm p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-500 mb-3">Click to upload PDF, images, or Excel files</p>
              <Input type="file" onChange={handleFileUpload} disabled={uploading} className="max-w-xs mx-auto" data-testid="intake-file-input" />
              {uploading && <p className="text-xs text-indigo-600 mt-2">Uploading...</p>}
            </div>
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Uploaded ({uploadedFiles.length})</p>
                {uploadedFiles.map((f, i) => (
                  <div key={f.name || i} className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-sm px-3 py-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800">{f.original_filename}</span>
                    <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(siteForm.has_dg ? 2 : 1)}>Back</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
                <Send className="w-4 h-4 mr-2" />{submitting ? 'Submitting...' : 'Submit All Data'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </Page>
  );
}

const Page = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-start justify-center p-4 pt-12">
    <div className="w-full max-w-2xl">{children}</div>
  </div>
);

const Card = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
    <div className="p-5 border-b border-slate-100 flex items-center space-x-3">
      <div className="w-10 h-10 bg-indigo-50 rounded-sm flex items-center justify-center"><Icon className="w-5 h-5 text-indigo-600" /></div>
      <h2 className="font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="p-5">{children}</div>
  </div>
);
